import { Box, Button, Card, CardContent, Container, Divider, Drawer, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Pagination, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import PropertyCard from '../components/objects/PropertyCard';
import useStyles from '../theme/useStyles';
import useLoading from '../hooks/useLoading';
import { searchProperties } from '../actions/PropertyAction';
import Swal from 'sweetalert2';
import { EditLocation, TableChart, ViewAgenda } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Properties = () => {
    const navigate = useNavigate();
    const classes = useStyles();
    const [expandedCards, setExpandedCards] = useState({});
    const { showLoading, hideLoading } = useLoading();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' o 'table'
    const [owners, setOwners] = useState([]);

    // Estado para la paginación y búsqueda
    const [requestParams, setRequestParams] = useState({
        pageNumber: 1,
        pageSize: 3, // Múltiplo de 3 para que queden completas las filas
        ownerId: '',
        name: '',
        address: '',
        priceMin: 0,
        priceMax: 0
    });

    // Estado para los datos y metadatos de paginación
    const [apiResponse, setApiResponse] = useState({
        properties: [],
        pageNumber: 1,
        pageSize: 9,
        totalRecords: 0,
        totalPages: 1
    });

    // Estado para los valores temporales de los filtros (antes de aplicar)
    const [filters, setFilters] = useState({
        ownerId: '',
        name: '',
        address: '',
        priceMin: '',
        priceMax: ''
    });

    const handlePageChange = (event, newPage) => {
        setRequestParams(prev => ({
            ...prev,
            pageNumber: newPage
        }));
    };

    const handlePageSizeChange = (event) => {
        const newPageSize = Number(event.target.value);
        setRequestParams(prev => ({
            ...prev,
            pageSize: newPageSize,
            pageNumber: 1 // Resetear a la primera página al cambiar el tamaño
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyFilters = () => {
        setRequestParams(prev => ({
            ...prev,
            pageNumber: 1, // Resetear a la primera página al aplicar nuevos filtros
            ownerId: filters.ownerId,
            name: filters.name,
            address: filters.address,
            priceMin: filters.priceMin ? Number(filters.priceMin) : null,
            priceMax: filters.priceMax ? Number(filters.priceMax) : null
        }));
        // setDrawerOpen(false); // Cerrar el drawer después de aplicar filtros
    };

    const resetFilters = () => {
        setFilters({
            name: '',
            address: '',
            priceMin: '',
            priceMax: ''
        });
        setRequestParams(prev => ({
            ...prev,
            pageNumber: 1,
            name: '',
            address: '',
            priceMin: null,
            priceMax: null
        }));
        // setDrawerOpen(false); // Cerrar el drawer después de resetear
    };

    const toggleViewMode = () => {
        setViewMode(prev => prev === 'cards' ? 'table' : 'cards');
    };

    useEffect(() => {
        const fetchOwners = async () => {
        try {
            const response = await axios.get('/api/owners');
            setOwners(response.data);
        } catch (error) {
            console.error('Error fetching owners:', error);
            Swal.fire('Error', 'No se pudo cargar la lista de propietarios', 'error');
        }
        };
        
        fetchOwners();
    }, []);


    useEffect(() => {
        const fetchProperties = async () => {
            showLoading('Cargando propiedades...');
            try {
                const response = await searchProperties(requestParams);
                
                if (response.status === 200) {
                    setApiResponse({
                        properties: response.data.data || [],
                        pageNumber: response.data.pageNumber || 1,
                        pageSize: response.data.pageSize || requestParams.pageSize,
                        totalRecords: response.data.totalRecords || 0,
                        totalPages: response.data.totalPages || 1
                    });
                } else {
                    Swal.fire({
                        title: 'Error',
                        text: 'No se pudieron cargar las propiedades',
                        icon: 'error'
                    });
                }
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error,
                    icon: 'error'
                });
            } finally {
                hideLoading();
            }
        };

        fetchProperties();
    }, [requestParams]);

    const toggleExpand = (id) => {
        setExpandedCards(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Componente para la vista de tabla
    const PropertiesTable = ({ properties }) => {
        return (
            <TableContainer component={Paper} sx={{ mt: 4, mb: 4 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Dirección</TableCell>
                            <TableCell>Precio</TableCell>
                            <TableCell>Año</TableCell>
                            <TableCell>Propietario</TableCell>
                            <TableCell>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {properties.map(property => (
                            <TableRow key={property.id}>
                                <TableCell>{property.name}</TableCell>
                                <TableCell>{property.address}</TableCell>
                                <TableCell>${property.price.toLocaleString()}</TableCell>
                                <TableCell>{property.year}</TableCell>
                                <TableCell>{property.ownerName}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="Actualizar propiedad">
                                            <IconButton
                                                onClick={() => navigate(`/propertyEdit/${property.id}`)}
                                                color="secondary"
                                            >
                                                <EditLocation />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Container className={classes.containermt} >
            {/* Botón flotante para cambiar vista */}
            {/* <IconButton
                color="primary"
                aria-label="cambiar vista"
                onClick={toggleViewMode}
                sx={{
                    position: 'fixed',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1200,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    '&:hover': {
                        backgroundColor: 'action.hover'
                    }
                }}
            >
                {viewMode === 'cards' ? <TableChart /> : <ViewAgenda />}
                <Typography variant="caption" sx={{ ml: 1 }}>
                    {viewMode === 'cards' ? 'Tabla' : 'Cards'}
                </Typography>
            </IconButton> */}
            <Box
                sx={{
                    position: 'fixed',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                {/* Etiqueta "Vista" */}
                <Typography
                    variant="caption"
                    sx={{
                        backgroundColor: 'white',
                        color: 'text.primary',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        boxShadow: 3,
                        fontWeight: 'bold'
                    }}
                >
                    Vista
                </Typography>

                {/* Botón flotante */}
                <IconButton
                    color="primary"
                    aria-label="cambiar vista"
                    onClick={toggleViewMode}
                    sx={{
                        backgroundColor: 'background.paper',
                        boxShadow: 3,
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                >
                    {viewMode === 'cards' ? <TableChart /> : <ViewAgenda />}
                    <Typography variant="caption" sx={{ ml: 1 }}>
                        {viewMode === 'cards' ? 'Tabla' : 'Cards'}
                    </Typography>
                </IconButton>
            </Box>


            {/* Botón flotante para abrir filtros */}
            <Box
                sx={{
                    position: 'fixed',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                {/* Etiqueta "Filtros" */}
                <Typography
                    variant="caption"
                    sx={{
                        backgroundColor: 'white',
                        color: 'text.primary',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        boxShadow: 3,
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap' // Evita que el texto se parta
                    }}
                >
                    Filtros
                </Typography>

                {/* Botón flotante */}
                <IconButton
                    color="primary"
                    aria-label="filtros"
                    onClick={() => setDrawerOpen(true)}
                    sx={{
                        backgroundColor: 'background.paper',
                        boxShadow: 3,
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                >
                    <FilterListIcon fontSize="large" />
                </IconButton>
            </Box>

            {/* Drawer (panel lateral) para los filtros */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 300,
                        boxSizing: 'border-box',
                        padding: 2
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Filtros de Búsqueda</Typography>
                    <IconButton onClick={() => setDrawerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth size="small" >
                        <InputLabel id="owner-select-label">Propietario</InputLabel>
                        <Select
                          labelId="owner-select-label"
                          name="ownerId"
                          value={filters.ownerId}
                          onChange={handleFilterChange}
                          label="Propietario"
                          sx={{ 
                            textAlign: 'left',
                            '& .MuiSelect-select': {
                              width: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }
                          }}
                        >
                          {owners.map(owner => (
                            <MenuItem 
                              key={owner.id} 
                              value={owner.id}
                              sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word'
                              }}
                            >
                              {owner.name}
                            </MenuItem>
                          ))}
                        </Select>
                    </FormControl>


                    <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        value={filters.name}
                        onChange={handleFilterChange}
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        fullWidth
                        label="Dirección"
                        name="address"
                        value={filters.address}
                        onChange={handleFilterChange}
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        fullWidth
                        label="Precio Mínimo"
                        name="priceMin"
                        type="number"
                        value={filters.priceMin}
                        onChange={handleFilterChange}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Precio Máximo"
                        name="priceMax"
                        type="number"
                        value={filters.priceMax}
                        onChange={handleFilterChange}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button 
                        variant="outlined" 
                        onClick={resetFilters}
                        sx={{ flex: 1, mr: 1 }}
                    >
                        Limpiar
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={applyFilters}
                        sx={{ flex: 1, ml: 1 }}
                    >
                        Aplicar
                    </Button>
                </Box>
            </Drawer>
            
            {/* Selector de registros por página */}
            {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
                <FormControl 
                    variant="outlined" 
                    size="small" 
                    sx={{ 
                        minWidth: 120,
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        '& .MuiOutlinedInput-root': {
                            padding: '3px', // Padding interno para el input
                        },
                        '& .MuiInputLabel-outlined': {
                            transform: 'translate(14px, 13px) scale(1)', // Posición inicial del label
                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)', // Posición cuando se eleva
                                backgroundColor: 'white', // Fondo blanco para tapar el borde
                                padding: '0 4px', // Pequeño padding horizontal para el label elevado
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent', // Quita el borde del input
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent', // Quita el borde en hover
                        },
                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent', // Quita el borde en focus
                        }
                    }}
                >
                    <InputLabel>Casas por pág.</InputLabel>
                    <Select
                        value={requestParams.pageSize}
                        onChange={handlePageSizeChange}
                        label="Registros por página"
                    >
                        <MenuItem value={3}>3</MenuItem>
                        <MenuItem value={6}>6</MenuItem>
                        <MenuItem value={9}>9</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={45}>45</MenuItem>
                    </Select>
                </FormControl>
            </Box> */}
<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
    <FormControl 
        variant="outlined" 
        size="small" 
        sx={{ 
            minWidth: 120,
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: '4px',
                boxShadow: 3,
                padding: '8px',
            },
            '& .MuiInputLabel-outlined': {
                color: 'text.primary',
                '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'white',
                    padding: '0 4px',
                    fontWeight: 'bold',
                    borderRadius: '4px',
                }
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
            }
        }}
    >
        <InputLabel sx={{ 
            backgroundColor: 'white', 
            px: 1, 
            borderRadius: '4px',
            boxShadow: 3,
            fontWeight: 'bold'
        }}>
            Casas por pág.
        </InputLabel>
        <Select
            value={requestParams.pageSize}
            onChange={handlePageSizeChange}
            label="Casas por pág."
        >
            <MenuItem value={3}>3</MenuItem>
            <MenuItem value={6}>6</MenuItem>
            <MenuItem value={9}>9</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={45}>45</MenuItem>
        </Select>
    </FormControl>
</Box>


            {/* Contenido según el modo de vista */}
            {viewMode === 'cards' ? (
                /* Listado de Propiedades */
                <Grid container spacing={4} sx={{ padding: 4 }}>
                    {apiResponse.properties.map(property => (
                        <Grid item xs={12} sm={6} lg={4} xl={3} key={property.id}>
                            <PropertyCard 
                                data={property} ok
                                expanded={expandedCards[property.id] || false} 
                                toggleExpand={() => toggleExpand(property.id)}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                /* Tabla de Propiedades */
                <PropertiesTable properties={apiResponse.properties} />
            )}

            {/* Paginador */}
            {apiResponse.totalPages > 1 && (
                <>
                    <div style={{ height: '20px' }} />
                    <Pagination 
                        count={apiResponse.totalPages} 
                        page={apiResponse.pageNumber} 
                        onChange={handlePageChange}
                        color="primary"
                        sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            mt: 4,
                            mb: 4
                        }}
                    />
                </>
            )}
        </Container>
    );
};

export default Properties;